import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { cx } from '@/styled-system/css';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import { getHaloPhotos } from 'app/shared/halo/utils/getHaloPhotos';
import { buildResponsiveImageAttrs } from 'app/shared/utils/fedPhotos';
import { buildListingPhotoAlt } from 'app/shared/utils/listingUtils';
import Card from '../../../../ui/Card';
import { ChevronLeftIcon, ChevronRightIcon } from '../../../../ui/icons';
import { Tabs } from '../../../../ui/Tabs';
import { PhotoLightbox } from '../../MediaCenter/PhotoLightbox';
import { TourLightbox } from '../../MediaCenter/TourLightbox';
import { TourPreviewCard } from '../../MediaCenter/TourPreviewCard';
import type { Floorplan, Unit } from '../UnitTable';
import * as styles from './unitDetailOverlay.styles';

type CarouselPhoto = { url: string; caption?: string };

type LayoutSectionProps = {
  unit: Unit;
  floorplan: Floorplan;
};

type LayoutTabId = 'floorplan' | 'threedtour' | 'photos';

export const LayoutSection = ({ unit, floorplan }: LayoutSectionProps) => {
  const listing = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing);
  const layoutCount = floorplan.layoutCount ?? 0;
  const floorPlanImageUrl =
    layoutCount > 0 ? (floorplan.largePhotos?.[0]?.url ?? floorplan.photos?.[0]?.url ?? null) : null;
  const tourUrl = floorplan.zillow3dTourUrl || null;
  const previewPhoto = getHaloPhotos(listing)[0];

  // Interior photos live after the layout diagrams in the concatenated photos array.
  // See FloorplanModel.ts: `modelImages = [...layouts, ...photos]`.
  const interiorPhotos: Array<CarouselPhoto> = (floorplan.largePhotos ?? floorplan.photos ?? [])
    .slice(layoutCount)
    .filter((p): p is CarouselPhoto => Boolean(p?.url));
  const hasInteriorPhotos = interiorPhotos.length > 0;

  const pickInitialTab = (): LayoutTabId => {
    if (floorPlanImageUrl) return 'floorplan';
    if (hasInteriorPhotos) return 'photos';
    return 'threedtour';
  };
  const [activeTabId, setActiveTabId] = useState<LayoutTabId>(pickInitialTab);
  const [tourOpen, setTourOpen] = useState(false);

  // Reset the active tab if the data behind it disappears (e.g. listing refetch
  // removes the tour URL while we're on the 3D Tour tab).
  useEffect(() => {
    const currentTabStillValid =
      (activeTabId === 'floorplan' && floorPlanImageUrl) ||
      (activeTabId === 'photos' && hasInteriorPhotos) ||
      (activeTabId === 'threedtour' && tourUrl);
    if (!currentTabStillValid) {
      if (floorPlanImageUrl) setActiveTabId('floorplan');
      else if (hasInteriorPhotos) setActiveTabId('photos');
      else setActiveTabId('threedtour');
    }
  }, [floorPlanImageUrl, tourUrl, hasInteriorPhotos, activeTabId]);

  if (!floorPlanImageUrl && !tourUrl && !hasInteriorPhotos) {
    return null;
  }

  const tabs: Array<{ id: LayoutTabId; label: string }> = [];
  if (floorPlanImageUrl) tabs.push({ id: 'floorplan', label: 'Floor Plan' });
  if (hasInteriorPhotos) tabs.push({ id: 'photos', label: 'Photos' });
  if (tourUrl) tabs.push({ id: 'threedtour', label: '3D Tour' });

  return (
    <section>
      <Card variant="default" size="md" title="The layout">
        <Tabs
          tabs={tabs}
          activeTabId={activeTabId}
          onTabChange={(id) => setActiveTabId(id as LayoutTabId)}
          ariaLabel="Layout views"
          idPrefix="layout-tab"
        />

        <div className={styles.layoutMediaContainer}>
          {activeTabId === 'floorplan' && floorPlanImageUrl && (
            <img
              src={floorPlanImageUrl}
              alt={`Floor plan for unit ${unit.name}`}
              className={styles.floorPlanImage}
              loading="lazy"
            />
          )}

          {activeTabId === 'photos' && hasInteriorPhotos && (
            <UnitPhotoCarousel photos={interiorPhotos} unitName={unit.name} />
          )}

          {activeTabId === 'threedtour' && tourUrl && (
            <TourPreviewCard previewPhoto={previewPhoto} onClick={() => setTourOpen(true)} label="3D Tour" />
          )}
        </div>

        <ul className={styles.unitSpecs} aria-label="Unit specifications">
          <li className={styles.specItem}>
            <span className={styles.specValue}>{unit.beds}</span> Beds
          </li>
          <li className={styles.specItem}>
            <span className={styles.specValue}>{unit.baths}</span> Baths
          </li>
          <li className={styles.specItem}>
            <span className={styles.specValue}>{unit.sqft.toLocaleString('en-US')}</span> sqft
          </li>
        </ul>
      </Card>

      {tourUrl && <TourLightbox tourUrl={tourUrl} open={tourOpen} onOpenChange={setTourOpen} />}
    </section>
  );
};

type UnitPhotoCarouselProps = {
  photos: Array<CarouselPhoto>;
  unitName: string;
};

const UnitPhotoCarousel = ({ photos, unitName }: UnitPhotoCarouselProps) => {
  const trackRef = useRef<HTMLUListElement>(null);
  const didScrollRef = useRef(false);
  const scrollIdleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    let rafId: number;
    const handleScroll = () => {
      // Flag that a scroll just happened so a click immediately after doesn't
      // trigger the lightbox (distinguishes swipe-drag from tap).
      didScrollRef.current = true;
      clearTimeout(scrollIdleTimerRef.current);
      scrollIdleTimerRef.current = setTimeout(() => {
        didScrollRef.current = false;
      }, 300);

      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const width = track.clientWidth;
        if (width === 0) return;
        setCurrentIndex(Math.round(track.scrollLeft / width));
      });
    };

    track.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      track.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
      clearTimeout(scrollIdleTimerRef.current);
    };
  }, []);

  const scrollToIndex = useCallback(
    (index: number) => {
      const track = trackRef.current;
      if (!track) return;
      const clamped = Math.max(0, Math.min(index, photos.length - 1));
      track.scrollTo({ left: clamped * track.clientWidth, behavior: 'instant' });
    },
    [photos.length],
  );

  // On resize/orientation change, realign the track to the current slide so the
  // counter and visible slide stay in sync with the new clientWidth.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const handleResize = () => {
      track.scrollTo({ left: currentIndex * track.clientWidth, behavior: 'instant' });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentIndex]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollToIndex(currentIndex + 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollToIndex(currentIndex - 1);
      }
    },
    [currentIndex, scrollToIndex],
  );

  const handleSlideClick = useCallback((index: number) => {
    if (didScrollRef.current) return;
    setLightboxIndex(index);
  }, []);

  const lightboxPhotos = photos.map((p) => ({ url: p.url, caption: p.caption ?? '' }));

  return (
    <>
      <section
        className={styles.photoCarousel}
        aria-roledescription="carousel"
        aria-label={`Unit ${unitName} photos`}
        onKeyDown={handleKeyDown}
      >
        <ul ref={trackRef} className={styles.photoCarouselTrack}>
          {photos.map((photo, i) => {
            const imageAttrs = buildResponsiveImageAttrs(photo.url, 768, [384, 768, 1152]);
            const alt = buildListingPhotoAlt(`Unit ${unitName}`, photo.caption, i);
            return (
              <li key={`${photo.url}-${i}`} className={styles.photoCarouselSlide}>
                <button
                  type="button"
                  className={styles.photoCarouselSlideButton}
                  onClick={() => handleSlideClick(i)}
                  aria-label={`Open photo ${i + 1} of ${photos.length}`}
                >
                  {imageAttrs ? (
                    <img
                      src={imageAttrs.src}
                      srcSet={imageAttrs.srcSet}
                      sizes="100vw"
                      alt={alt}
                      className={styles.photoCarouselImage}
                      loading={Math.abs(i - currentIndex) <= 1 ? 'eager' : 'lazy'}
                      draggable={false}
                    />
                  ) : (
                    <img
                      src={photo.url}
                      alt={alt}
                      className={styles.photoCarouselImage}
                      loading={Math.abs(i - currentIndex) <= 1 ? 'eager' : 'lazy'}
                      draggable={false}
                    />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
        {photos.length > 1 && (
          <>
            <button
              type="button"
              className={cx(styles.photoCarouselArrowButton, styles.photoCarouselArrowButtonPrev)}
              onClick={() => scrollToIndex(currentIndex - 1)}
              disabled={currentIndex === 0}
              aria-label="Previous photo"
            >
              <ChevronLeftIcon />
            </button>
            <button
              type="button"
              className={cx(styles.photoCarouselArrowButton, styles.photoCarouselArrowButtonNext)}
              onClick={() => scrollToIndex(currentIndex + 1)}
              disabled={currentIndex === photos.length - 1}
              aria-label="Next photo"
            >
              <ChevronRightIcon />
            </button>
          </>
        )}

        <div
          className={styles.photoCarouselCounter}
          role="status"
          aria-live="polite"
          aria-label={`Photo ${currentIndex + 1} of ${photos.length}`}
        >
          {currentIndex + 1} / {photos.length}
        </div>
      </section>

      <PhotoLightbox
        photos={lightboxPhotos}
        initialIndex={lightboxIndex ?? 0}
        open={lightboxIndex !== null}
        onOpenChange={(next) => {
          if (!next) setLightboxIndex(null);
        }}
      />
    </>
  );
};
